import type { App } from "@open-booking/server";
import type { BulkDayPriceDefaultsPayload, BulkDayPriceOverridePayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetDefaultsResponse = App["~Routes"]["properties"][":id"]["day-pricing"]["defaults"]["get"]["response"]["200"];
type SetDefaultsResponse = App["~Routes"]["properties"][":id"]["day-pricing"]["defaults"]["put"]["response"]["200"];
type GetOverridesResponse = App["~Routes"]["properties"][":id"]["day-pricing"]["overrides"]["get"]["response"]["200"];
type BulkSetOverridesResponse = App["~Routes"]["properties"][":id"]["day-pricing"]["overrides"]["bulk"]["post"]["response"]["200"];
type DeleteOverrideResponse = App["~Routes"]["properties"][":id"]["day-pricing"]["overrides"][":date"]["delete"]["response"]["200"];

export type DayPriceDefault = GetDefaultsResponse["defaults"][number];
export type DayPriceOverride = GetOverridesResponse["overrides"][number];

interface OverridesQueryArg {
	propertyId: string;
	from?: string;
	to?: string;
}

export const propertyDayPriceApi = createApi({
	reducerPath: "propertyDayPriceApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["DayPricingDefaults", "DayPricingOverrides"],
	endpoints: (builder) => ({
		getDayPriceDefaults: builder.query<GetDefaultsResponse, string>({
			query:
				(propertyId): EdenCall<GetDefaultsResponse> =>
				(client) =>
					client.properties({ id: propertyId })["day-pricing"].defaults.get(),
			providesTags: ["DayPricingDefaults"],
		}),
		setDayPriceDefaults: builder.mutation<SetDefaultsResponse, { propertyId: string; data: BulkDayPriceDefaultsPayload }>({
			query:
				({ propertyId, data }): EdenCall<SetDefaultsResponse> =>
				(client) =>
					client.properties({ id: propertyId })["day-pricing"].defaults.put(data),
			invalidatesTags: ["DayPricingDefaults"],
		}),
		getDayPriceOverrides: builder.query<GetOverridesResponse, OverridesQueryArg>({
			query:
				({ propertyId, from, to }): EdenCall<GetOverridesResponse> =>
				(client) =>
					client.properties({ id: propertyId })["day-pricing"].overrides.get({
						query: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
					}),
			providesTags: ["DayPricingOverrides"],
		}),
		bulkSetDayPriceOverrides: builder.mutation<BulkSetOverridesResponse, { propertyId: string; data: BulkDayPriceOverridePayload }>({
			query:
				({ propertyId, data }): EdenCall<BulkSetOverridesResponse> =>
				(client) =>
					client.properties({ id: propertyId })["day-pricing"].overrides.bulk.post(data),
			invalidatesTags: ["DayPricingOverrides"],
		}),
		deleteDayPriceOverride: builder.mutation<DeleteOverrideResponse, { propertyId: string; date: string }>({
			query:
				({ propertyId, date }): EdenCall<DeleteOverrideResponse> =>
				(client) =>
					client.properties({ id: propertyId })["day-pricing"].overrides({ date }).delete(),
			invalidatesTags: ["DayPricingOverrides"],
		}),
	}),
});

export const {
	useGetDayPriceDefaultsQuery,
	useSetDayPriceDefaultsMutation,
	useGetDayPriceOverridesQuery,
	useBulkSetDayPriceOverridesMutation,
	useDeleteDayPriceOverrideMutation,
} = propertyDayPriceApi;
